import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Chip,
    MenuList,
    MenuItem,
    Paper,
    Popover,
    lighten,
    useTheme,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Check, ChevronDown } from 'react-feather';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import useStore from 'src/hooks/useStore';
import { DEVELOPER_LEVEL_ACCESS } from 'src/components/access';
import type { TConversationAdvancedFilters } from '../../constants/conversationAdvancedFilters';
import {
    ALL_TAGS_FILTER_ID,
    collectUniqueStoreTags,
    getAccessibleStoresForUser,
    normalizeStoreTagName,
} from '../../utils/storeTags';

export type TConversationStoreTag = {
    _id: string;
    name: string;
    icon: string;
    color?: string;
};

type TTagFilterOption = TConversationStoreTag & { isAll?: boolean };

export const Tags = ({
    advancedFilters,
    onAdvancedFiltersChange,
}: {
    advancedFilters: TConversationAdvancedFilters;
    onAdvancedFiltersChange: (filters: TConversationAdvancedFilters) => void;
}) => {
    const [open, setOpen] = useState(false);
    const [isLoadingTags, setIsLoadingTags] = useState(false);
    const handleToggleOpen = () => setOpen(!open);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const theme = useTheme();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { stores, getStores } = useStore();

    const isDeveloper = user?.accessLevel === DEVELOPER_LEVEL_ACCESS;

    useEffect(() => {
        if (!isDeveloper) return;

        let isMounted = true;
        setIsLoadingTags(true);

        Promise.resolve(getStores()).finally(() => {
            if (isMounted) setIsLoadingTags(false);
        });

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDeveloper]);

    const accessibleStores = useMemo(
        () => getAccessibleStoresForUser(user, stores),
        [user, stores]
    );

    const storeTagFilters = useMemo(
        () => collectUniqueStoreTags(accessibleStores),
        [accessibleStores]
    );

    const allTagOption: TTagFilterOption = {
        _id: ALL_TAGS_FILTER_ID,
        name: t('Chat.MediaGalleryAll'),
        icon: '',
        isAll: true,
    };

    const visibleStoreTags = isLoadingTags ? [] : storeTagFilters.slice(0, 2);
    const remainingTags = isLoadingTags ? [] : storeTagFilters.slice(2);
    const missingVisibleTagSlots = Math.max(0, 2 - visibleStoreTags.length);
    const selectedTagNames = advancedFilters.tags.map((tag) =>
        normalizeStoreTagName(tag.value)
    );
    const hasTagFilter = selectedTagNames.length > 0;
    const hasSelectedRemainingTag = remainingTags.some((tag) =>
        selectedTagNames.includes(normalizeStoreTagName(tag.name))
    );

    const handleTagClick = (tag: TTagFilterOption) => {
        if (tag._id === ALL_TAGS_FILTER_ID) {
            onAdvancedFiltersChange({
                ...advancedFilters,
                tags: [],
            });
            setOpen(false);
            return;
        }

        onAdvancedFiltersChange({
            ...advancedFilters,
            tags: [{ label: tag.name, value: tag.name }],
        });
        setOpen(false);
    };

    const isTagActive = (tag: TTagFilterOption) => {
        if (tag._id === ALL_TAGS_FILTER_ID) {
            return !hasTagFilter;
        }

        return selectedTagNames.includes(normalizeStoreTagName(tag.name));
    };

    const renderTagIcon = (tag: TTagFilterOption) => {
        if (!tag.icon) return null;

        return <span style={{ marginRight: 4 }}>{tag.icon}</span>;
    };

    const renderTagChip = (tag: TTagFilterOption) => {
        const isActive = isTagActive(tag);

        return (
            <Chip
                variant={isActive ? 'default' : 'outlined'}
                key={tag._id}
                label={
                    <Box display="flex" alignItems="center">
                        {renderTagIcon(tag)}
                        {tag.name}
                    </Box>
                }
                size="small"
                style={{
                    maxWidth: 120,
                    fontSize: 12,
                    padding: '1px 1px',
                    backgroundColor: isActive
                        ? tag.color
                            ? lighten(tag.color, 0.5)
                            : lighten(theme.palette.primary.main, 0.5)
                        : 'transparent',
                    color: isActive
                        ? theme.palette.getContrastText(
                              tag.color
                                  ? lighten(tag.color, 0.5)
                                  : lighten(theme.palette.primary.main, 0.5)
                          )
                        : 'inherit',
                }}
                onClick={() => handleTagClick(tag)}
            />
        );
    };

    return (
        <Fragment>
            <Popover
                open={open}
                onClose={handleToggleOpen}
                anchorEl={buttonRef.current}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Paper>
                    <MenuList>
                        {remainingTags.map((option) => (
                            <MenuItem
                                key={option._id}
                                onClick={() => handleTagClick(option)}
                                selected={isTagActive(option)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                }}
                            >
                                {renderTagIcon(option)}
                                {option.name}
                                {isTagActive(option) && (
                                    <Check
                                        size={16}
                                        color={theme.palette.primary.main}
                                        style={{ marginLeft: 'auto' }}
                                    />
                                )}
                            </MenuItem>
                        ))}
                    </MenuList>
                </Paper>
            </Popover>
            <Box
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    padding: '0px 10px',
                    gap: 4,
                }}
            >
                <Box
                    style={{
                        display: 'flex',
                        gap: 2,
                        overflow: 'scroll',
                        maxWidth: '83%',
                    }}
                >
                    {renderTagChip(allTagOption)}
                    {visibleStoreTags.map((tag) => renderTagChip(tag))}
                    {Array.from(
                        { length: missingVisibleTagSlots },
                        (_, index) => (
                            <Box
                                key={`tag-skeleton-${index}`}
                                style={{
                                    flex: '1 1 0',
                                    minWidth: 0,
                                }}
                            >
                                <Skeleton
                                    variant="rect"
                                    width="100%"
                                    height={32}
                                    animation="wave"
                                    style={{ borderRadius: 999 }}
                                />
                            </Box>
                        )
                    )}
                </Box>

                <Chip
                    variant={hasSelectedRemainingTag ? 'default' : 'outlined'}
                    onClick={handleToggleOpen}
                    ref={buttonRef as any}
                    color={hasSelectedRemainingTag ? 'primary' : 'default'}
                    size="small"
                    label={
                        <Box display="flex" alignItems="center">
                            {hasSelectedRemainingTag && (
                                <Check style={{ width: 16, height: 16 }} />
                            )}
                            <ChevronDown style={{ width: 18, height: 18 }} />
                        </Box>
                    }
                    style={{
                        maxWidth: 120,
                        fontSize: 12,
                        padding: '1px 1px',
                    }}
                />
            </Box>
        </Fragment>
    );
};
